import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Select, Card, DataTable, Modal } from "../components/ui";

const MACROCATEGORIE = [
  { value: "100000000", label: "Manutenzione" },
  { value: "100000001", label: "Servizi" },
  { value: "100000002", label: "Forniture" },
  { value: "100000003", label: "Consulenza" },
];

const CLASSIFICAZIONE_LABELS = {
  100000000: "A - Eccellente",
  100000001: "B - Adeguato",
  100000002: "C - Critico",
  100000003: "D - Inadeguato",
};

const CLASSIFICAZIONE_COLORS = {
  100000000: "bg-green-100 text-green-800",
  100000001: "bg-blue-100 text-blue-800",
  100000002: "bg-orange-100 text-orange-800",
  100000003: "bg-red-100 text-red-800",
};

const STATO_LABELS = {
  100000000: "Bozza",
  100000001: "Completata",
  100000002: "Approvata",
};

const STATO_COLORS = {
  100000000: "bg-gray-100 text-gray-700",
  100000001: "bg-yellow-100 text-yellow-800",
  100000002: "bg-green-100 text-green-800",
};

function ValutazioneFornitoriPage() {
  const { apiRequest, user, canPerformAction } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [valutazioni, setValutazioni] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Filtri
  const [filterStato, setFilterStato] = useState("");
  const [filterClassificazione, setFilterClassificazione] = useState("");
  const [filterPeriodo, setFilterPeriodo] = useState("");
  const [filterMacrocategoria, setFilterMacrocategoria] = useState("");

  // Approvazione massiva
  const [approving, setApproving] = useState(false);

  // Form nuova valutazione
  const [selectedFornitore, setSelectedFornitore] = useState("");
  const [selectedPeriodo, setSelectedPeriodo] = useState("");

  // Genera semestre
  const [generating, setGenerating] = useState(false);
  const [genResults, setGenResults] = useState(null);

  const canCompile =
    canPerformAction("valutazioni.compila") || user?.ruolo === 100000000;
  const isAdmin = user?.ruolo === 100000000;

  useEffect(() => {
    loadData();
  }, [filterStato, filterClassificazione, filterPeriodo, filterMacrocategoria]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStato) params.stato = filterStato;
      if (filterClassificazione) params.classificazione = filterClassificazione;
      if (filterPeriodo) params.periodo = filterPeriodo;
      if (filterMacrocategoria) params.macrocategoria = filterMacrocategoria;

      const [valutazioniData, dashboardData] = await Promise.all([
        apiRequest(
          `/valutazioni-fornitori${Object.keys(params).length ? "?" + new URLSearchParams(params) : ""}`,
        ),
        apiRequest("/valutazioni-fornitori/dashboard"),
      ]);

      setValutazioni(valutazioniData || []);
      setDashboard(dashboardData);
    } catch (error) {
      console.error("Error loading data:", error);
      showToast("Errore nel caricamento dati", "error");
    } finally {
      setLoading(false);
    }
  };

  const openNewModal = async () => {
    try {
      const data = await apiRequest("/valutazioni-fornitori/scadenze");
      setFornitori(data || []);

      // Default periodo corrente
      const now = new Date();
      const year = now.getFullYear();
      const half = now.getMonth() < 6 ? "H1" : "H2";
      setSelectedPeriodo(`${year}-${half}`);
      setSelectedFornitore("");
      setNewModalOpen(true);
    } catch (error) {
      showToast("Errore nel caricamento fornitori", "error");
    }
  };

  const handleApprovaTutte = async () => {
    if (!confirm("Approvare tutte le valutazioni completate?")) return;
    setApproving(true);
    try {
      const now = new Date();
      const year = now.getFullYear();
      const half = now.getMonth() < 6 ? "H1" : "H2";
      const result = await apiRequest("/valutazioni-fornitori/approva-tutte", {
        method: "PUT",
        body: JSON.stringify({ periodo: `${year}-${half}` }),
      });
      showToast(`${result.approvate} valutazioni approvate`, "success");
      loadData();
    } catch (error) {
      showToast(error.message || "Errore nell'approvazione", "error");
    } finally {
      setApproving(false);
    }
  };

  const handleGeneraSemestre = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const half = now.getMonth() < 6 ? "H1" : "H2";
    const periodo = `${year}-${half}`;

    setGenerating(true);
    setGenResults(null);
    try {
      const result = await apiRequest(
        "/valutazioni-fornitori/genera-semestre",
        {
          method: "POST",
          body: JSON.stringify({ periodo }),
        },
      );
      setGenResults(result);
      showToast(
        `Generazione completata: ${result.creati} create, ${result.saltati} già esistenti, ${result.errori} errori`,
        result.errori > 0 ? "warning" : "success",
      );
      loadData();
    } catch (error) {
      showToast(error.message || "Errore nella generazione", "error");
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateValutazione = async () => {
    if (!selectedFornitore || !selectedPeriodo) {
      showToast("Seleziona fornitore e periodo", "error");
      return;
    }

    setCreating(true);
    try {
      const result = await apiRequest(
        `/fornitori/${selectedFornitore}/valutazioni`,
        {
          method: "POST",
          body: JSON.stringify({ periodo: selectedPeriodo }),
        },
      );
      showToast(
        `Valutazione creata per ${result.fornitore} (${result.criteriGenerati} criteri)`,
        "success",
      );
      setNewModalOpen(false);
      navigate(`/valutazione-fornitori/${result.valutazioneId}`);
    } catch (error) {
      showToast(error.message || "Errore nella creazione", "error");
    } finally {
      setCreating(false);
    }
  };

  const getMacrocategoriaLabel = (value) => {
    const found = MACROCATEGORIE.find((m) => m.value === String(value));
    return found ? found.label : "-";
  };

  // Genera opzioni periodo (ultimi 4 semestri)
  const periodoOptions = (() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 4; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i * 6, 1);
      const y = d.getFullYear();
      const h = d.getMonth() < 6 ? "H1" : "H2";
      const val = `${y}-${h}`;
      if (!options.find((o) => o.value === val)) {
        options.push({
          value: val,
          label: `${val} (${h === "H1" ? "Gen-Giu" : "Lug-Dic"} ${y})`,
        });
      }
    }
    return options;
  })();

  const columns = [
    {
      key: "phc_name",
      label: "Valutazione",
      sortable: true,
      render: (row) => (
        <button
          onClick={() =>
            navigate(`/valutazione-fornitori/${row.phc_valutazionefornitoreid}`)
          }
          className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left"
        >
          {row.phc_name}
        </button>
      ),
    },
    {
      key: "phc_periodo",
      label: "Periodo",
      sortable: true,
    },
    {
      key: "phc_macrocategoria",
      label: "Categoria",
      sortable: true,
      render: (row) => (
        <span className="text-sm px-2 py-0.5 rounded bg-blue-50 text-blue-700">
          {getMacrocategoriaLabel(row.phc_macrocategoria)}
        </span>
      ),
    },
    {
      key: "phc_punteggiofinale",
      label: "Punteggio",
      sortable: true,
      render: (row) => (
        <span className="font-mono font-bold">
          {row.phc_punteggiofinale != null ? row.phc_punteggiofinale : "-"}
        </span>
      ),
    },
    {
      key: "phc_classificazione",
      label: "Classificazione",
      sortable: true,
      render: (row) =>
        row.phc_classificazione != null ? (
          <span
            className={`text-xs font-semibold px-2 py-1 rounded ${CLASSIFICAZIONE_COLORS[row.phc_classificazione] || ""}`}
          >
            {CLASSIFICAZIONE_LABELS[row.phc_classificazione] || "-"}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: "phc_stato",
      label: "Stato",
      sortable: true,
      render: (row) => (
        <span
          className={`text-xs px-2 py-1 rounded ${STATO_COLORS[row.phc_stato] || ""}`}
        >
          {STATO_LABELS[row.phc_stato] || "-"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Valutazione Fornitori
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Valutazione periodica fornitori ISO 9001:2015
          </p>
        </div>
        {canCompile && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleGeneraSemestre}
              disabled={generating}
            >
              {generating ? "Generazione..." : "Genera Semestre"}
            </Button>
            {isAdmin && dashboard?.stati?.completata > 0 && (
              <Button
                variant="outline"
                onClick={handleApprovaTutte}
                disabled={approving}
              >
                {approving
                  ? "Approvazione..."
                  : `Approva Tutte (${dashboard.stati.completata})`}
              </Button>
            )}
            <Button onClick={openNewModal}>+ Singola</Button>
          </div>
        )}
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">
              {dashboard.totaleValutazioni}
            </p>
            <p className="text-xs text-gray-500">
              Valutazioni {dashboard.periodoCorrente}
            </p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {dashboard.classificazioni?.A || 0}
            </p>
            <p className="text-xs text-gray-500">Classe A</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {dashboard.classificazioni?.B || 0}
            </p>
            <p className="text-xs text-gray-500">Classe B</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">
              {dashboard.classificazioni?.C || 0}
            </p>
            <p className="text-xs text-gray-500">Classe C</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">
              {dashboard.classificazioni?.D || 0}
            </p>
            <p className="text-xs text-gray-500">Classe D</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">
              {dashboard.fornitoriValutati}/{dashboard.totaleFornitori}
            </p>
            <p className="text-xs text-gray-500">Valutati</p>
          </Card>
          <Card className="p-4 text-center border-amber-200 bg-amber-50">
            <p className="text-2xl font-bold text-amber-700">
              {dashboard.fornitoriDaValutare}
            </p>
            <p className="text-xs text-amber-600">Da Valutare</p>
          </Card>
        </div>
      )}

      {/* Risultati generazione semestre */}
      {genResults && (
        <Card className="p-4 border-blue-200 bg-blue-50">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-blue-900">
              Risultato Generazione Semestre
            </h3>
            <button
              onClick={() => setGenResults(null)}
              className="text-blue-400 hover:text-blue-600 text-sm"
            >
              Chiudi
            </button>
          </div>
          <div className="flex gap-6 mb-3 text-sm">
            <span className="text-green-700 font-medium">
              {genResults.creati} create
            </span>
            <span className="text-gray-600">
              {genResults.saltati} già esistenti
            </span>
            {genResults.carryForward > 0 && (
              <span className="text-purple-600 font-medium">
                {genResults.carryForward} con carry-forward
              </span>
            )}
            {genResults.errori > 0 && (
              <span className="text-red-600 font-medium">
                {genResults.errori} errori
              </span>
            )}
          </div>
          {genResults.dettagli?.length > 0 && (
            <details>
              <summary className="text-xs text-blue-600 cursor-pointer mb-2">
                Dettaglio ({genResults.dettagli.length} fornitori)
              </summary>
              <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                {genResults.dettagli.map((d, i) => (
                  <div
                    key={i}
                    className={`flex justify-between px-2 py-1 rounded ${d.errore ? "bg-red-50 text-red-700" : "bg-white"}`}
                  >
                    <span className="font-medium">{d.fornitore}</span>
                    {d.errore ? (
                      <span className="text-red-500">{d.errore}</span>
                    ) : (
                      <span className="text-gray-500">
                        {d.punteggio != null
                          ? `${d.punteggio} (${d.classificazione})`
                          : "In attesa"}
                        {d.autoCompletata && (
                          <span className="ml-1 text-green-600">Auto</span>
                        )}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-48">
            <Select
              label="Stato"
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value)}
              options={[
                { value: "", label: "Tutti" },
                { value: "100000000", label: "Bozza" },
                { value: "100000001", label: "Completata" },
                { value: "100000002", label: "Approvata" },
              ]}
            />
          </div>
          <div className="w-48">
            <Select
              label="Classificazione"
              value={filterClassificazione}
              onChange={(e) => setFilterClassificazione(e.target.value)}
              options={[
                { value: "", label: "Tutte" },
                { value: "100000000", label: "A - Eccellente" },
                { value: "100000001", label: "B - Adeguato" },
                { value: "100000002", label: "C - Critico" },
                { value: "100000003", label: "D - Inadeguato" },
              ]}
            />
          </div>
          <div className="w-56">
            <Select
              label="Periodo"
              value={filterPeriodo}
              onChange={(e) => setFilterPeriodo(e.target.value)}
              options={[{ value: "", label: "Tutti" }, ...periodoOptions]}
            />
          </div>
          <div className="w-48">
            <Select
              label="Macrocategoria"
              value={filterMacrocategoria}
              onChange={(e) => setFilterMacrocategoria(e.target.value)}
              options={[{ value: "", label: "Tutte" }, ...MACROCATEGORIE]}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <DataTable
          columns={columns}
          data={valutazioni}
          loading={loading}
          emptyMessage="Nessuna valutazione trovata"
          searchable
          searchPlaceholder="Cerca valutazione..."
          searchKeys={["phc_name"]}
        />
      </Card>

      {/* New Valutazione Modal */}
      <Modal
        isOpen={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        title="Nuova Valutazione Fornitore"
        size="md"
      >
        <div className="space-y-4">
          {fornitori.length > 0 ? (
            <>
              <p className="text-sm text-gray-600">
                {fornitori.length} fornitori da valutare nel periodo
                selezionato.
              </p>
              <Select
                label="Fornitore *"
                value={selectedFornitore}
                onChange={(e) => setSelectedFornitore(e.target.value)}
                options={[
                  { value: "", label: "-- Seleziona fornitore --" },
                  ...fornitori.map((f) => ({
                    value: f.phc_fornitoreid,
                    label: `${f.phc_name} (${getMacrocategoriaLabel(f.phc_macrocategoria)})`,
                  })),
                ]}
              />
              <Select
                label="Periodo *"
                value={selectedPeriodo}
                onChange={(e) => setSelectedPeriodo(e.target.value)}
                options={periodoOptions}
              />
            </>
          ) : (
            <p className="text-sm text-green-600 font-medium">
              Tutti i fornitori attivi sono già stati valutati per il periodo
              corrente.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setNewModalOpen(false)}>
              Annulla
            </Button>
            {fornitori.length > 0 && (
              <Button onClick={handleCreateValutazione} disabled={creating}>
                {creating ? "Creazione..." : "Crea Valutazione"}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ValutazioneFornitoriPage;
